import React from 'react';
import { Link } from 'react-router-dom';

import {
  Drawer as MuiDrawer,
  styled,
  DrawerProps,
} from '@mui/material';

import SidebarFooter from './SidebarFooter';
import SidebarNav from './SidebarNav';

import pingwireLogo from '../../../assets/logos/pingwire-negative.png';
import pingwireLogoMark from '../../../assets/logos/logo-mark-white.svg';
import { Page } from './SidebarNavList';

export const drawerWidth = 258;
export const collapsedDrawerWidth = 64;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>`
  border-right: 0;

  & .MuiDrawer-paper {
    width: ${(props) => (props.collapsed ? collapsedDrawerWidth : drawerWidth)}px;
    border-right: 0;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      width: 0px;
    }
  }
`;

const Brand = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.customSidebar.header.color};
  background-color: ${(props) => props.theme.customSidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }
  &:hover {
    background-color: ${(props) => props.theme.customSidebar.header.background};
  }
`;

// The collapsed strip keeps the 64px brand square but drops its padding, which
// would otherwise be wider than the strip itself.
const CollapsedBrand = styled(Brand)`
  padding-left: 0;
  padding-right: 0;
`;

const BrandLogo = styled('img')(({ theme }) => ({
  marginRight: theme.spacing(2),
  height: 32,
}));

const BrandLogoMark = styled('img')({
  height: 32,
});

const Sidebar = ({
  items,
  variant = 'permanent',
  open,
  collapsed = false,
  onClose,
}: {
  items: {
    title: string;
    pages: Page[];
  }[];
  variant: DrawerProps['variant'];
  open: boolean;
  collapsed?: boolean;
  onClose?: () => void;
}) => (
  <Drawer
    variant={variant}
    open={open}
    onClose={onClose}
    collapsed={collapsed}
  >
    {collapsed ? (
      <CollapsedBrand to="/">
        <BrandLogoMark src={pingwireLogoMark} alt="Pingwire" />
      </CollapsedBrand>
    ) : (
      <Brand to="/">
        <BrandLogo src={pingwireLogo} alt="Pingwire" />
      </Brand>
    )}
    <SidebarNav onClose={onClose} items={items} collapsed={collapsed} />
    <SidebarFooter collapsed={collapsed} />
  </Drawer>
);

export default Sidebar;
